import type { Page } from '@servicetim/shared';
import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Keyset paging over `Page<T>` responses (`?pageSize=&cursor=`). Pages are appended, so a
 * `DataTable` shows "Load more" instead of numbered pages (cursors are opaque and forward-only).
 */

export interface PagedListOptions<T> {
  queryKey: QueryKey;
  /** Fetches one page; `cursor` is undefined for the first page. */
  fetchPage: (cursor: string | undefined, signal: AbortSignal) => Promise<Page<T>>;
  enabled?: boolean;
  staleTime?: number;
}

export type PagedListResult<T> = UseInfiniteQueryResult<InfiniteData<Page<T>>, Error> & {
  /** Every loaded page flattened, in order. */
  items: T[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

export function usePagedList<T>(options: PagedListOptions<T>): PagedListResult<T> {
  const query = useInfiniteQuery({
    queryKey: options.queryKey,
    queryFn: ({ pageParam, signal }) => options.fetchPage(pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options.enabled,
    staleTime: options.staleTime,
  });
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);
  return {
    ...query,
    items,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/** Default page size for admin lists; the API caps at 200. */
export const DEFAULT_PAGE_SIZE = 50;

export interface PageParams {
  pageSize?: number;
  cursor?: string;
}

/** Builds the `?pageSize=&cursor=` query for one page request. */
export const pageQuery = (cursor: string | undefined, pageSize = DEFAULT_PAGE_SIZE) => ({
  pageSize,
  cursor,
});
